import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { TimeoutError } from '@/lib/errors';
import { apiGet } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import type { ViewStateResponse } from './types';

/** Stable empty reference so callers get a consistent identity while loading. */
const EMPTY_SET = new Set<string>();

// Caps simultaneous in-flight traffic probes so an org with many webhooks
// doesn't open unbounded parallel requests; 6 matches the conservative
// HTTP/1.1 per-domain connection-limit baseline browsers historically enforce.
const MAX_CONCURRENT_TRAFFIC_PROBES = 6;

/**
 * Detects which of the given webhook entities have received traffic (at
 * least one `ahoy_webhook_received` timeline event), aggregating one
 * presence probe per entity into a single React Query. There is no batched
 * multi-entity events endpoint, so each entity is probed individually via
 * `/view-state/`, chunked to at most `MAX_CONCURRENT_TRAFFIC_PROBES`
 * concurrent requests — `Promise.allSettled` per chunk ensures one failing
 * probe only drops that entity's indicator rather than the whole list or
 * the query.
 */
export function useWebhookTrafficPresence(
  orgId: number,
  entityIds: string[]
): { trafficIds: Set<string>; isLoading: boolean } {
  // Entity ids are UUIDs and never contain commas, so a sorted comma-joined
  // string is a safe, stable scalar cache key.
  const entityIdsKey = useMemo(
    () => [...entityIds].sort().join(','),
    [entityIds]
  );

  const query = useQuery({
    queryKey: ['webhook-traffic-presence', orgId, entityIdsKey],
    enabled: orgId > 0 && entityIdsKey.length > 0,
    queryFn: async ({ signal }) => {
      const ids = entityIdsKey.split(',');
      const result = new Set<string>();
      let rejectedCount = 0;
      let timeoutCount = 0;
      let otherCount = 0;

      for (
        let start = 0;
        start < ids.length;
        start += MAX_CONCURRENT_TRAFFIC_PROBES
      ) {
        const chunk = ids.slice(start, start + MAX_CONCURRENT_TRAFFIC_PROBES);
        const settled = await Promise.allSettled(
          chunk.map(id =>
            apiGet<ViewStateResponse>(
              `/api/orgs/${orgId}/crm-events/entities/${id}/view-state/`,
              {
                event_type_name: 'ahoy_webhook_received',
                limit: 1,
                include_associated: false,
              },
              10000,
              signal
            )
          )
        );
        settled.forEach((s, i) => {
          if (s.status === 'fulfilled') {
            if ((s.value.events?.length ?? 0) > 0) {
              result.add(chunk[i]);
            }
          } else {
            rejectedCount += 1;
            if (s.reason instanceof TimeoutError) {
              timeoutCount += 1;
            } else {
              otherCount += 1;
            }
          }
        });
      }

      if (rejectedCount > 0) {
        let errorReason: 'timeout' | 'other' | 'mixed';
        if (timeoutCount === 0) {
          errorReason = 'other';
        } else if (otherCount === 0) {
          errorReason = 'timeout';
        } else {
          errorReason = 'mixed';
        }
        logger.warn(
          `Webhook traffic presence: ${rejectedCount} of ${ids.length} checks failed`,
          {
            component: 'crm_events',
            org_id: orgId,
            error_reason: errorReason,
          }
        );
      }
      return result;
    },
  });

  return useMemo(
    () => ({ trafficIds: query.data ?? EMPTY_SET, isLoading: query.isLoading }),
    [query.data, query.isLoading]
  );
}
